import { useParams, Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { MapPin, Building2, CreditCard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatXof } from "@/lib/format";
import { getEcole, getMotifsForEcole } from "@/lib/api";

export default function EcoleDetail() {
  const params = useParams();
  const ecoleId = params.ecoleId as string;

  const { data: ecole, isLoading: ecoleLoading } = useQuery({
    queryKey: ["ecole", ecoleId],
    queryFn: () => getEcole(ecoleId),
    enabled: !!ecoleId,
  });

  const { data: motifs, isLoading: motifsLoading } = useQuery({
    queryKey: ["ecole", ecoleId, "motifs"],
    queryFn: () => getMotifsForEcole(ecoleId),
    enabled: !!ecoleId,
  });

  if (ecoleLoading)
    return <div className="p-12 text-center text-navy/50">Chargement…</div>;
  if (!ecole)
    return <div className="p-12 text-center text-red-500">École introuvable.</div>;

  return (
    <div className="max-w-4xl mx-auto px-6 py-12">
      <div className="bg-white rounded-3xl border border-orange-50 shadow-sm mb-12 overflow-hidden">
        <div className="relative h-40 md:h-48 overflow-hidden bg-navy">
          <img
            src="/images/ecoles-banner.webp"
            alt="École catholique"
            className="absolute inset-0 w-full h-full object-cover object-center"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-navy/70 via-navy/20 to-transparent" />
        </div>
        <div className="flex items-start gap-6 p-8 -mt-12 relative">
          <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center text-coral flex-shrink-0 shadow-md border border-orange-50">
            <Building2 size={32} />
          </div>
          <div className="pt-12">
            <h1 className="text-3xl font-display font-bold text-navy mb-2">
              {ecole.denomination}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-navy/60">
              <span className="flex items-center gap-1">
                <MapPin size={16} /> {ecole.communeNom ?? "·"}
                {ecole.quartier ? `, ${ecole.quartier}` : ""}
              </span>
              {ecole.codeEtablissementSedec && (
                <span>
                  Code SEDEC: <strong>{ecole.codeEtablissementSedec}</strong>
                </span>
              )}
              {ecole.typeEnseignement && (
                <span className="bg-slate-100 px-2 py-1 rounded text-xs font-bold">
                  {ecole.typeEnseignement}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <h2 className="text-2xl font-display font-bold text-navy mb-6">
        Catalogue des paiements
      </h2>

      {motifsLoading ? (
        <div className="grid sm:grid-cols-2 gap-4 animate-pulse">
          <div className="h-32 bg-white rounded-xl" />
          <div className="h-32 bg-white rounded-xl" />
        </div>
      ) : motifs && motifs.length > 0 ? (
        <div className="grid sm:grid-cols-2 gap-4">
          {motifs.map((motif) => (
            <div
              key={motif.id}
              className="bg-white rounded-xl p-6 border border-orange-50 flex flex-col justify-between"
            >
              <div>
                <div className="flex justify-between items-start mb-2">
                  <span className="text-xs font-bold text-navy/50 uppercase tracking-wider">
                    {motif.categorie}
                  </span>
                  {motif.periode && (
                    <span className="bg-orange-50 text-navy px-2 py-0.5 rounded text-xs font-bold">
                      {motif.periode}
                    </span>
                  )}
                </div>
                <h3 className="font-bold text-navy text-lg mb-2">
                  {motif.libelle}
                </h3>
                {motif.montantXof != null ? (
                  <p className="text-xl font-display text-navy mb-6">
                    {formatXof(motif.montantXof)}
                  </p>
                ) : (
                  // Motif tarifé par classe : le montant exact s'affiche une fois
                  // l'enfant choisi sur /payer. Jamais « 0 F CFA ».
                  <p className="text-sm font-bold text-navy/60 mb-6">
                    Montant selon la classe de l'enfant
                  </p>
                )}
              </div>
              <Link
                href={`/payer?ecoleId=${ecole.id}&motifId=${motif.id}`}
                className="w-full"
              >
                <Button className="w-full bg-coral hover:bg-[#d16b50] text-white rounded-xl font-bold flex items-center justify-center gap-2">
                  <CreditCard size={18} /> Payer ce motif
                </Button>
              </Link>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl p-12 text-center border border-orange-50">
          <p className="text-navy/60 font-medium">
            Catalogue en préparation. Veuillez contacter l'école.
          </p>
        </div>
      )}
    </div>
  );
}
